import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { Heading, Container } from '../theme/primitives';
import { iconMap, CheckmarkFilled } from '../theme/icons';
import { fititBrandRamp, stonePalette } from '../theme/brand';
import { API_HOST, API_BASE } from '../config.js';
import heroImg1 from '../assets/img/hero/hero1-new.jpg';
import heroImg2 from '../assets/img/hero/hero2-new.jpg';
import heroImg3 from '../assets/img/hero/hero3-new.jpg';
import heroImg4 from '../assets/img/hero/hero4-new.jpg';
import heroImg5 from '../assets/img/hero/hero5-new.jpg';
import requestConsultImg from '../assets/img/marketing/request-consult.jpg';
import ContactForm from '../components/ContactForm';
import ProjectGallery from '../components/shop/ProjectGallery';
import Testimonials from '../components/shop/Testimonials';
import HowToChoosePanels from '../components/shop/HowToChoosePanels';
import PartnerLogos from '../components/shop/PartnerLogos';
import { Card } from '../theme/primitives';

const ViewInArIcon = iconMap.view_in_ar;
const ArrowForwardIcon = iconMap.arrow_forward;
const ChevronRightIcon = iconMap.chevron_right;
const ChevronLeftIcon = iconMap.chevron_left;
const CheckIcon = iconMap.check;
const ChairIcon = iconMap.chair;

// Tamsūs sekcijų fonai (statistikos juosta, konfigūratoriaus banneris, atsiliepimai)
// yra sąmoningi dekoratyvūs gradientai/spalvos, ne semantinės Fluent spalvos — tekstas
// ant jų lieka literaliomis balta/rgba reikšmėmis (tas pats paternas, kaip
// NavigationBar.jsx ir Footer.jsx, nes app-lygio Fluent tema yra ŠVIESI, o
// `tokens.colorNeutralForeground*` skaičiuojami pagal ją, ne pagal lokalų foną).
const useStyles = makeStyles({
  // ── HERO ─────────────────────────────────────────────────────────────────
  heroSection: { backgroundColor: tokens.colorNeutralBackground1 },
  heroContainer: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: '2.5rem',
    paddingBottom: '2rem',
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem', paddingTop: '3.5rem', paddingBottom: '0.5rem' },
  },
  heroGrid: {
    // minmax(0, 1fr), ne vien 1fr — kitaip trustRow (overflow-x:auto su nowrap
    // vaikais) vis tiek priverstų grid takelį išsiplėsti pagal turinio min-content
    // plotį, "ištempdamas" visą stulpelį (ir su juo — antraštę) už viewport ribų.
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '4rem',
    alignItems: 'center',
    // Dešinė (mozaikos) kolona padidinta, kairė sumažinta (žr. /admin/bugs
    // "Padidink ploti paveiksliuku"). Patikrinta Playwright'u: TIKSLIAI ties
    // 1024px slenksčiu trustRow (žemiau, overflow-x:auto su nowrap vaikais)
    // nebetelpa į savo (dabar siauresnę) koloną — bet minmax(0, ...) VIS TIEK
    // apsaugo, kad tai liktų tik trustRow VIDINIS horizontalus slinkimas
    // (numatytas, žr. jo pačio overflowX:auto), NE viso puslapio ar stulpelio
    // išstūmimas už viewport ribų. Platesniuose desktop plotyje (1280px+)
    // abu stulpeliai turi pakankamai vietos ir trustRow telpa be slinkimo.
    '@media (min-width: 1024px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.15fr)', gap: '4rem' },
  },
  heroCopy: { maxWidth: '36rem' },
  heroTitle: {
    fontSize: '2.6rem',
    lineHeight: '1.1',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    letterSpacing: '-0.02em',
    marginTop: 0,
    marginBottom: tokens.spacingVerticalL,
    '@media (min-width: 640px)': { fontSize: '3rem' },
    '@media (min-width: 1024px)': { fontSize: '3.2rem' },
  },
  heroTitleAccent: { color: tokens.colorBrandForeground2 },
  heroSubtitle: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.6',
    marginTop: 0,
    marginBottom: tokens.spacingVerticalXXL,
  },
  heroButtons: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXXL,
    '@media (min-width: 480px)': { flexDirection: 'row' },
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingTop: tokens.spacingVerticalMNudge,
    paddingBottom: tokens.spacingVerticalMNudge,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusMedium,
    textDecorationLine: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
    ':active': { transform: 'scale(0.98)' },
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingTop: tokens.spacingVerticalMNudge,
    paddingBottom: tokens.spacingVerticalMNudge,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    textDecorationLine: 'none',
    cursor: 'pointer',
    userSelect: 'none',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorNeutralBackground4 },
    ':active': { transform: 'scale(0.98)' },
  },
  // Po 480px trustRow tampa horizontaliai slenkančia eilute (žr. žemiau) — bet
  // PAČIAME siauriausiame (mobiliame) plotyje 3 tekstai netilpdavo į vieną
  // ekraną be scrollbar'o (žr. /admin/bugs "Turi matytis visi 3 tekstai, dabar
  // atsiranda scrollbar, mobiliame juos isdestyti stulpeliu") — po vieną
  // stulpelį jie visi matomi iškart, be jokio slinkimo.
  trustRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    rowGap: tokens.spacingVerticalXS,
    '@media (min-width: 480px)': {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      columnGap: tokens.spacingHorizontalL,
      overflowX: 'auto',
    },
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  trustCheckIcon: { color: tokens.colorBrandForeground2, flexShrink: 0, fontSize: '14px' },

  // ── HERO — dešinės pusės mozaika (tik desktop) ──────────────────────────
  heroMosaic: { display: 'none', width: '100%', '@media (min-width: 1024px)': { display: 'block' } },
  heroMosaicRow: { display: 'flex', gap: '11px', alignItems: 'flex-start' },
  heroCol1: { flex: '1.45', height: '430px', marginTop: '24px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroCol2: { flex: '1.1', height: '515px', marginTop: 0, display: 'flex', flexDirection: 'column' },
  heroCol3: { flex: '0.82', height: '462px', marginTop: '40px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroImgBox: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusXLarge,
    ':hover img': { transform: 'scale(1.05)' },
  },
  heroImgBoxFlex1: { flex: 1 },
  heroImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transitionProperty: 'transform',
    transitionDuration: tokens.durationSlow,
  },
  // ── HERO — mobiliojo vaizdo blokas ──────────────────────────────────────
  heroMobileImage: {
    display: 'block',
    marginTop: tokens.spacingVerticalL,
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalXXL})`,
    marginRight: `calc(-1 * ${tokens.spacingHorizontalXXL})`,
    overflow: 'hidden',
    maxHeight: '260px',
    '@media (min-width: 640px)': { borderRadius: tokens.borderRadiusXLarge, marginLeft: 0, marginRight: 0 },
    '@media (min-width: 1024px)': { display: 'none' },
  },
  heroMobileImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', maxHeight: '260px' },

  // ── bendra sekcijos apvija ───────────────────────────────────────────────
  sectionPadX: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  sectionPadY: { paddingTop: '5rem', paddingBottom: '5rem' },
  reveal: {
    transitionProperty: 'opacity, transform',
    transitionDuration: '700ms',
    transitionTimingFunction: 'ease-out',
  },
  revealHidden: { opacity: 0, transform: 'translateY(2rem)' },
  revealVisible: { opacity: 1, transform: 'translateY(0)' },

  sectionHeaderRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalXXL },
  viewAllLink: {
    display: 'none',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground3,
    textDecorationLine: 'none',
    transitionProperty: 'color',
    transitionDuration: tokens.durationNormal,
    ':hover': { color: tokens.colorBrandForeground2 },
    '@media (min-width: 640px)': { display: 'inline-flex' },
  },

  // ── KAIP TAI VEIKIA ──────────────────────────────────────────────────────
  // Be antraštinio teksto, be dekoratyvaus fono paveikslėlio IR be pilkos
  // sekcijos fono spalvos (vartotojo prašymu) — grynai balta, stepsCard pati
  // savaime (su šešėliu) pakankamai atsiskiria nuo puslapio fono.
  stepsSection: { position: 'relative', overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground1 },
  stepsContentLayer: { position: 'relative', zIndex: 1 },
  // Vienas pakeltas Card-tipo konteineris, viduje 5 lygios "stotelės", atskirtos
  // plonu bordiuku (ne 5 plūduriuojantys apskritimai + banguojantis SVG kelias,
  // kaip ankstesnėje versijoje) — švaresnis, "produkto roadmap" stiliaus
  // sprendimas: didelis blankus numeris (01-05) neša sekos jausmą, be
  // rankomis skaičiuojamos bezjė kreivės geometrijos.
  stepsCard: {
    position: 'relative', display: 'grid', gridTemplateColumns: '1fr',
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`, boxShadow: tokens.shadow8, overflow: 'hidden',
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(5, 1fr)' },
  },
  stepCell: {
    position: 'relative', display: 'flex', flexDirection: 'column',
    paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalXL, paddingBottom: tokens.spacingVerticalXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
    '@media (min-width: 1024px)': { borderBottom: 'none', borderRight: `1px solid ${tokens.colorNeutralStroke2}` },
  },
  stepCellLast: {
    borderBottom: 'none',
    '@media (min-width: 1024px)': { borderRight: 'none' },
  },
  // Tarpelio strelyte tarp "stoteliu" (tik desktop'e, ant bordiuko) - maza,
  // dekoratyvi nuoroda i seka, be sunkios SVG kelio geometrijos.
  stepArrow: {
    display: 'none',
    '@media (min-width: 1024px)': {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'absolute', top: '2.25rem', right: '-13px', width: '26px', height: '26px',
      borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3,
      fontSize: '14px', zIndex: 2,
    },
  },
  stepNumeral: {
    fontSize: '2rem', fontWeight: tokens.fontWeightBold, lineHeight: 1,
    color: fititBrandRamp[140], marginBottom: tokens.spacingVerticalS, fontVariantNumeric: 'tabular-nums',
  },
  stepIconCircle: {
    width: '44px', height: '44px', borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: tokens.colorBrandForeground2, fontSize: '20px', marginBottom: tokens.spacingVerticalM, flexShrink: 0,
  },
  // Neužpildytas (outline) apskritimas su varnele — ne solid žalias fonas (žr.
  // /admin/bugs "Pakeisk sita icon i varnele (ne filled)").
  stepEndCircle: {
    backgroundColor: 'transparent', color: fititBrandRamp[140],
    border: `2px solid ${fititBrandRamp[140]}`,
  },
  stepTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXXS, fontSize: tokens.fontSizeBase400 },
  stepDesc: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, lineHeight: '1.55', margin: 0 },

  // ── IŠSKIRTI BALDAI ──────────────────────────────────────────────────────
  productsSection: { backgroundColor: stonePalette[50] },
  // auto-fit + minmax (VIENA taisyklė, be sluoksniuotų @media tai pačiai savybei —
  // žr. Footer.jsx grid komentarą apie Griffel runtime tvarkos riziką) — mažesnės
  // kortelės, natūraliai susitalpina 6-8 per eilutę plačiuose ekranuose (žr. #4).
  // Anksčiau CSS Grid (auto-fit, minmax(180px,1fr)) — siaurame ekrane minmax
  // tilpdavo TIK 1 koloną per eilutę, tad ilgas sąrašas virsdavo vienu ilgu
  // stulpeliu vietoj horizontalaus slinkimo (žr. /admin/bugs "Populiarus baldai
  // turi buti kaip slider, o ne vienas po kito stulpeliu") — paverstas į tą patį
  // horizontal-scroll+snap paterną, kokį jau naudoja PartnerLogos.jsx.
  productsRelative: { position: 'relative' },
  productsScroller: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    paddingBottom: '2px',
    '::-webkit-scrollbar': { display: 'none' },
  },
  productsNavButton: {
    display: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1,
    '@media (min-width: 640px)': { display: 'flex' },
  },
  productsNavButtonLeft: { left: '-1rem' },
  productsNavButtonRight: { right: '-1rem' },
  productsNavIcon: { fontSize: '18px' },
  productCard: {
    display: 'block',
    flex: '0 0 auto',
    width: '220px',
    scrollSnapAlign: 'start',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    textDecorationLine: 'none',
    transitionProperty: 'opacity, transform, border-color, box-shadow',
    transitionDuration: '700ms',
    transitionTimingFunction: 'ease-out',
    ':hover': { borderColor: tokens.colorBrandStroke2, boxShadow: tokens.shadow8, transform: 'translateY(-3px)' },
    ':hover img': { transform: 'scale(1.05)' },
  },
  productImgWrap: { aspectRatio: '1 / 1', backgroundColor: tokens.colorNeutralBackground2, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: tokens.spacingHorizontalM },
  productImg: { width: '100%', height: '100%', objectFit: 'contain', transitionProperty: 'transform', transitionDuration: tokens.durationSlow },
  // Realistiška "kambario" nuotrauka (lifestyleImagePath) užpildo visą kortelę be
  // paddingo (žr. FurnitureList.jsx imageCover) — ne plika 3D atvaizdas ant tuščio
  // fono su paddingu, kaip anksčiau (žr. /admin/bugs "Populiarus baldai home page
  // turi rodyti realistiskas foto o ne 3d").
  productImgWrapCover: { padding: 0 },
  productImgCover: { objectFit: 'cover' },
  productImgFallback: { width: '100%', height: '100%', display: 'none', alignItems: 'center', justifyContent: 'center' },
  productImgFallbackIcon: { color: tokens.colorNeutralStroke3, fontSize: '48px' },
  productInfo: { padding: tokens.spacingHorizontalM, backgroundColor: tokens.colorNeutralBackground1, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  productCode: { fontFamily: 'monospace', fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  productDims: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, marginTop: '2px' },
  productViewRow: {
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorBrandForeground2,
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },

  // ── VERTYBĖS ──────────────────────────────────────────────────────────────
  // stonePalette[100] — TA PATI neutrali "stone" šeima, kaip steps/contact/products/
  // materials sekcijos (žr. #2: sekcijos turi dertis tarpusavyje), o ne atskira,
  // niekur kitur nekartojama žalia prekės ženklo spalva. Prekės ženklo žalia lieka
  // TIK akcentams (valueIconChip, hover borderColor), ne visos sekcijos fonui.
  valuesSection: { backgroundColor: stonePalette[100] },
  valuesGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL, '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(3, 1fr)' } },
  valueCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '2rem',
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    transitionProperty: 'opacity, transform, box-shadow, border-color',
    transitionDuration: '700ms',
    transitionTimingFunction: 'ease-out',
    ':hover': { boxShadow: tokens.shadow8, borderColor: tokens.colorBrandStroke2, transform: 'translateY(-3px)' },
  },
  valueIconChip: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorBrandForeground2,
    fontSize: '24px',
  },
  valueTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalXS, marginTop: 0 },
  valueDesc: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.6', margin: 0 },

  // ── UŽKLAUSOS FORMA ──────────────────────────────────────────────────────
  contactSection: { backgroundColor: stonePalette[50] },
  contactGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: '3.5rem', alignItems: 'stretch',
    '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr', gap: '4.5rem' },
  },
  contactImageWrap: {
    display: 'none', borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    '@media (min-width: 1024px)': { display: 'block' },
  },
  contactImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  contactCard: { maxWidth: '34rem', marginLeft: 'auto', marginRight: 'auto', '@media (min-width: 1024px)': { marginLeft: 0, marginRight: 0 } },

  // ── MEDŽIAGOS / FURNITŪRA / GAMYBA ───────────────────────────────────────
  materialsSection: { backgroundColor: tokens.colorNeutralBackground1 },

  // ── KAIP IŠSIRINKTI PLOKŠTES (juosta) ───────────────────────────────────
  // stonePalette[50] anksčiau kūrė antrą, silpnai kitokio atspalvio foną aplink
  // pačios kortelės (stonePalette[100]) apvalius kampus — matėsi kaip siaura
  // "dubliuoto" fono juostelė prie krašto. Ta pati balta, kaip kaimyninės
  // sekcijos, panaikina šį dvigubą sluoksnį (žr. /admin/bugs "Dubliuojasi
  // background, padaryk ta balta kuris eina apacioje sio bloko").
  howToSection: { backgroundColor: tokens.colorNeutralBackground1 },
  materialsGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL, '@media (min-width: 768px)': { gridTemplateColumns: 'repeat(3, 1fr)' } },
  materialsCard: {
    padding: '2rem', borderRadius: tokens.borderRadiusXLarge, border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'box-shadow, border-color, transform', transitionDuration: tokens.durationNormal,
    ':hover': { boxShadow: tokens.shadow8, borderColor: tokens.colorBrandStroke2, transform: 'translateY(-3px)' },
  },
  materialsIconChip: {
    width: '48px', height: '48px', borderRadius: tokens.borderRadiusXLarge, backgroundColor: tokens.colorBrandBackground2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: tokens.spacingVerticalL,
    color: tokens.colorBrandForeground2, fontSize: '22px',
  },
  materialsTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalM, marginTop: 0 },
  materialsList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, margin: 0, padding: 0, listStyle: 'none' },
  materialsListItem: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.5' },
  materialsListIcon: { color: tokens.colorBrandForeground2, flexShrink: 0, fontSize: '16px', marginTop: '2px' },
});

function Home() {
  const { t } = useTranslation();
  const s = useStyles();
  const [featured, setFeatured] = useState([]);
  const [visible, setVisible] = useState({});
  const productsScrollRef = useRef(null);
  const scrollProductsBy = (dir) => productsScrollRef.current?.scrollBy({ left: dir * 460, behavior: 'smooth' });

  useEffect(() => { document.title = t('home.doc_title'); }, [t]);

  useEffect(() => {
    // Pirmiausia bandome TIK administratoriaus rankiniu būdu pažymėtus "Populiarūs
    // baldai" elementus (žr. /admin/bugs "administravime turi buti imanoma pazymeti
    // kurie baldai bus rodomi home puslapyje") — jei dar nė vienas nepažymėtas,
    // krentame atgal prie ankstesnio elgesio (paskutiniai 6 parduotuvės elementai),
    // kad Home puslapis netaptų tuščias, kol administratorius dar nieko nepažymėjo.
    fetch(`${API_BASE}/furnitures/shop?featured=1`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const featured = Array.isArray(data) ? data : [];
        if (featured.length > 0) { setFeatured(featured.slice(0, 6)); return; }
        return fetch(`${API_BASE}/furnitures/shop`)
          .then(r => r.ok ? r.json() : [])
          .then(all => setFeatured((Array.isArray(all) ? all : []).slice(0, 6)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) setVisible(p => ({ ...p, [e.target.dataset.section]: true }));
      }),
      { threshold: 0.07 }
    );
    document.querySelectorAll('[data-section]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [featured]);

  // "Kliento kelionė" — tiksliai tie patys 4 žingsniai kaip tikroje fitit.lt svetainėje
  // (užklausa → konfigūravimas → meistro vizitas → gamyba), ne mano ankstesnė
  // "kaip veikia konfigūratorius" versija — vartotojas paprašė būtent šio turinio.
  const JOURNEY_STEPS = [
    { icon: 'mail',              title: t('home.journey.step1_title'), desc: t('home.journey.step1_desc') },
    { icon: 'design_services',   title: t('home.journey.step2_title'), desc: t('home.journey.step2_desc') },
    { icon: 'person',            title: t('home.journey.step3_title'), desc: t('home.journey.step3_desc') },
    { icon: 'storefront',        title: t('home.journey.step4_title'), desc: t('home.journey.step4_desc') },
  ];
  const JOURNEY_END_NODES = [...JOURNEY_STEPS, { icon: 'check', title: t('home.journey.step5_title'), desc: '', isEnd: true }];

  const VALUES = [
    { icon: 'workspace_premium',       title: t('home.values.0_title'), desc: t('home.values.0_desc') },
    { icon: 'precision_manufacturing', title: t('home.values.1_title'), desc: t('home.values.1_desc') },
    { icon: 'verified',                title: t('home.values.2_title'), desc: t('home.values.2_desc') },
  ];

  const MATERIALS = [
    {
      icon: 'layers', title: t('home.materials.plates_title'),
      items: [
        t('home.materials.plates_1'),
        t('home.materials.plates_2'),
        t('home.materials.plates_3'),
      ],
    },
    {
      icon: 'settings', title: t('home.materials.hardware_title'),
      items: [
        t('home.materials.hardware_1'),
        t('home.materials.hardware_2'),
        t('home.materials.hardware_3'),
      ],
    },
    {
      icon: 'precision_manufacturing', title: t('home.materials.production_title'),
      items: [
        t('home.materials.production_1'),
        t('home.materials.production_2'),
        t('home.materials.production_3'),
      ],
    },
  ];

  return (
    <div>

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className={s.heroSection}>
        <Container size="xl" className={s.heroContainer}>
          <div className={s.heroGrid}>

            {/* Left — copy */}
            <div className={s.heroCopy}>
              <h1 className={s.heroTitle}>
                {t('home.hero.title_1')}<br />
                <span className={s.heroTitleAccent}>{t('home.hero.title_2')}</span><br />
                {t('home.hero.title_3')}
              </h1>

              <p className={s.heroSubtitle}>
                {t('home.hero.subtitle')}
              </p>

              <div className={s.heroButtons}>
                <Link to="/konfiguratorius" className={s.btnPrimary}>
                  <ViewInArIcon fontSize={18} />
                  {t('home.hero.btn_configure')}
                </Link>
                <Link to="/meistro-uzsakymas" className={s.btnSecondary}>
                  {t('home.hero.btn_carpenter')}
                  <ArrowForwardIcon fontSize={16} />
                </Link>
              </div>

              <div className={s.trustRow}>
                {[
                  t('home.hero.trust_warranty'),
                  t('home.hero.trust_production'),
                  t('home.hero.trust_delivery'),
                ].map(text => (
                  <div key={text} className={s.trustItem}>
                    <CheckIcon className={s.trustCheckIcon} />
                    {text}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — staggered mosaic (desktop only), clean photos without text overlays */}
            <div className={s.heroMosaic}>
              <div className={s.heroMosaicRow}>

                {/* ── Col 1 ── */}
                <div className={s.heroCol1}>
                  <div className={s.heroImgBox} style={{ height: '30%' }}>
                    <img src={heroImg1} alt={t('home.hero.img1_alt')} className={s.heroImg} />
                  </div>
                  <div className={mergeClasses(s.heroImgBox, s.heroImgBoxFlex1)}>
                    <img src={heroImg2} alt={t('home.hero.img2_alt')} className={s.heroImg} />
                  </div>
                </div>

                {/* ── Col 2 ── */}
                <div className={s.heroCol2}>
                  <div className={mergeClasses(s.heroImgBox, s.heroImgBoxFlex1)}>
                    <img src={heroImg3} alt={t('home.hero.img3_alt')} className={s.heroImg} />
                  </div>
                </div>

                {/* ── Col 3 ── */}
                <div className={s.heroCol3}>
                  <div className={s.heroImgBox} style={{ height: '46%' }}>
                    <img src={heroImg4} alt={t('home.hero.img4_alt')} className={s.heroImg} />
                  </div>
                  <div className={mergeClasses(s.heroImgBox, s.heroImgBoxFlex1)}>
                    <img src={heroImg5} alt={t('home.hero.img5_alt')} className={s.heroImg} />
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Mobile hero image — shown below text on small screens */}
          <div className={s.heroMobileImage}>
            <img src={heroImg3} alt={t('home.hero.img_mobile_alt')} className={s.heroMobileImg} />
          </div>
        </Container>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────── */}
      <section data-section="steps" className={mergeClasses(s.stepsSection, s.sectionPadX, s.sectionPadY)}>
        <Container size="xl" className={s.stepsContentLayer}>
          <div className={s.stepsCard}>
            {JOURNEY_END_NODES.map((step, idx) => {
              const isLast = idx === JOURNEY_END_NODES.length - 1;
              return (
                <div
                  key={step.title}
                  className={mergeClasses(s.stepCell, isLast && s.stepCellLast, s.reveal, visible.steps ? s.revealVisible : s.revealHidden)}
                  style={{ transitionDelay: visible.steps ? `${idx * 90}ms` : '0ms' }}
                >
                  {/* Steps 1-4: just the plain numeral, no icon badge — the mail/design/
                      person/storefront icons duplicated what the title already said (žr.
                      /admin/bugs "Istrink situs icon, o vietoj 05 padaryk Check icon").
                      Step 5 (isEnd): a check icon instead of "05" — it's an endpoint, not
                      a numbered step. */}
                  {step.isEnd ? (
                    <div className={mergeClasses(s.stepIconCircle, s.stepEndCircle)}>
                      <CheckmarkFilled fontSize={20} />
                    </div>
                  ) : (
                    <span className={s.stepNumeral}>{String(idx + 1).padStart(2, '0')}</span>
                  )}
                  <h3 className={s.stepTitle}>{step.title}</h3>
                  {step.desc && <p className={s.stepDesc}>{step.desc}</p>}
                  {!isLast && <span className={s.stepArrow} aria-hidden="true"><ChevronRightIcon fontSize={14} /></span>}
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─── VALUES ───────────────────────────────────────────── */}
      <section data-section="values" className={mergeClasses(s.valuesSection, s.sectionPadX, s.sectionPadY)}>
        <Container size="xl">
          <div className={s.valuesGrid}>
            {VALUES.map(({ icon, title, desc }, idx) => {
              const Icon = iconMap[icon];
              return (
                <div
                  key={title}
                  className={mergeClasses(s.valueCard, visible.values ? s.revealVisible : s.revealHidden)}
                  style={{ transitionDelay: visible.values ? `${idx * 90}ms` : '0ms' }}
                >
                  <div className={s.valueIconChip}>
                    {Icon && <Icon fontSize={24} />}
                  </div>
                  <h3 className={s.valueTitle}>{title}</h3>
                  <p className={s.valueDesc}>{desc}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─── UŽKLAUSOS FORMA ──────────────────────────────────── */}
      <section className={mergeClasses(s.contactSection, s.sectionPadX, s.sectionPadY)}>
        <Container size="xl">
          <div className={s.contactGrid}>
            <Card padding={tokens.spacingHorizontalXXXL} className={s.contactCard}>
              <ContactForm />
            </Card>
            <div className={s.contactImageWrap}>
              <img src={requestConsultImg} alt={t('send_request.img_alt')} className={s.contactImage} />
            </div>
          </div>
        </Container>
      </section>

      {/* ─── ĮGYVENDINTI PROJEKTAI ────────────────────────────── */}
      <ProjectGallery />

      {/* ─── KLIENTŲ ATSILIEPIMAI ─────────────────────────────── */}
      {/* Tas pats bendras Testimonials komponentas, kaip /baldai/:code puslapyje —
          pakeičia anksčiau čia buvusią atskirą "Klientų patirtys" sekciją (savo
          tamsiu fonu, 3 kortelių tinkleliu, atskiru REVIEWS masyvu). */}
      <Testimonials />

      {/* ─── FEATURED FURNITURE ───────────────────────────────── */}
      {featured.length > 0 && (
        <section data-section="products" className={mergeClasses(s.productsSection, s.sectionPadX, s.sectionPadY)}>
          <Container size="xl">
            <div className={s.sectionHeaderRow}>
              <div>
                <Heading as="h2" variant="h2">{t('home.products.title')}</Heading>
              </div>
              <Link to="/baldai" className={s.viewAllLink}>
                {t('home.products.view_all')}
                <ArrowForwardIcon fontSize={16} />
              </Link>
            </div>

            <div className={s.productsRelative}>
              <div ref={productsScrollRef} className={s.productsScroller}>
                {featured.map((f, idx) => {
                  const lifestyleSrc = f.lifestyleImagePath ? `${API_HOST}${f.lifestyleImagePath}` : null;
                  const src = lifestyleSrc || (f.imagePath ? `${API_HOST}${f.imagePath}` : null);
                  const isLifestyle = !!lifestyleSrc;
                  return (
                  <Link
                    key={f._id}
                    to={`/baldai/${f.code}`}
                    className={mergeClasses(s.productCard, visible.products ? s.revealVisible : s.revealHidden)}
                    style={{ transitionDelay: visible.products ? `${idx * 70}ms` : '0ms' }}
                  >
                    <div className={mergeClasses(s.productImgWrap, isLifestyle && s.productImgWrapCover)}>
                      <img
                        src={src}
                        alt={f.code}
                        className={mergeClasses(s.productImg, isLifestyle && s.productImgCover)}
                        onError={e => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                      />
                      <div className={s.productImgFallback}>
                        <ChairIcon className={s.productImgFallbackIcon} />
                      </div>
                    </div>
                    <div className={s.productInfo}>
                      <div className={s.productCode}>{f.code}</div>
                      {f.settings?.generalWidth && (
                        <div className={s.productDims}>
                          {(f.settings.generalWidth * 100).toFixed(0)} × {(f.settings.generalHeight * 100).toFixed(0)} cm
                        </div>
                      )}
                      <div className={s.productViewRow}>
                        {t('home.products.view')}
                        <ArrowForwardIcon fontSize={13} />
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
              <Button
                appearance="outline"
                shape="circular"
                onClick={() => scrollProductsBy(-1)}
                aria-label={t('shop.partner_logos.scroll_left')}
                icon={<ChevronLeftIcon className={s.productsNavIcon} />}
                className={mergeClasses(s.productsNavButton, s.productsNavButtonLeft)}
              />
              <Button
                appearance="outline"
                shape="circular"
                onClick={() => scrollProductsBy(1)}
                aria-label={t('shop.partner_logos.scroll_right')}
                icon={<ChevronRightIcon className={s.productsNavIcon} />}
                className={mergeClasses(s.productsNavButton, s.productsNavButtonRight)}
              />
            </div>
          </Container>
        </section>
      )}

      {/* ─── MEDŽIAGOS / FURNITŪRA / GAMYBA ──────────────────── */}
      <section className={mergeClasses(s.materialsSection, s.sectionPadX, s.sectionPadY)}>
        <Container size="xl">
          <div className={s.materialsGrid}>
            {MATERIALS.map(({ icon, title, items }) => {
              const Icon = iconMap[icon];
              return (
                <div key={title} className={s.materialsCard}>
                  <div className={s.materialsIconChip}>
                    {Icon && <Icon fontSize={22} />}
                  </div>
                  <h3 className={s.materialsTitle}>{title}</h3>
                  <ul className={s.materialsList}>
                    {items.map(item => (
                      <li key={item} className={s.materialsListItem}>
                        <CheckIcon className={s.materialsListIcon} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─── KAIP IŠSIRINKTI PLOKŠTES ─────────────────────────── */}
      {/* Pats komponentas neturi pilno pločio fono (žr. HowToChoosePanels.jsx —
          naudojamas ir /baldai/:code, kur toks fonas nereikalingas) — čia,
          Home sekcijų ritme, apgaubiama savo juosta, kad dertų su likusiomis. */}
      <div className={s.howToSection}>
        <HowToChoosePanels />
      </div>

      {/* ─── PARTNERIŲ LOGOTIPAI ──────────────────────────────── */}
      <PartnerLogos />

    </div>
  );
}

export default Home;
